import type { AllergyIntolerance, CodeableConcept } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import { CodeSelectInput, StringInput } from '../../components/inputs';
import { useFhirEditState } from '../../core/FhirEditScope';
import type { FhirView } from '../../core/view-contract';

/**
 * Edit/add variant for AllergyIntolerance. Terminology is day-zero simple:
 * substance and reaction are free text (`CodeableConcept.text`), the two
 * required-binding code fields are closed selects. ValueSet-backed typeahead
 * replaces the free text later without changing this view's shape.
 */

const CLINICAL_STATUS_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical';

const CLINICAL_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'resolved', label: 'Resolved' },
] as const;

const CRITICALITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'high', label: 'High' },
  { value: 'unable-to-assess', label: 'Unable to assess' },
] as const;

type Criticality = NonNullable<AllergyIntolerance['criticality']>;
type ClinicalStatusCode = (typeof CLINICAL_STATUS_OPTIONS)[number]['value'];

function clinicalStatusConcept(code: string): CodeableConcept {
  return { coding: [{ system: CLINICAL_STATUS_SYSTEM, code }] };
}

function reactionText(data: AllergyIntolerance): string | undefined {
  return data.reaction?.[0]?.manifestation?.[0]?.text;
}

function withReaction(
  data: AllergyIntolerance,
  text: string | undefined,
): AllergyIntolerance {
  // undefined (not a null) — stripped from diffs and draft payloads.
  if (!text) return { ...data, reaction: undefined };
  return { ...data, reaction: [{ manifestation: [{ text }] }] };
}

export const AllergyEditView: FhirView<AllergyIntolerance> = ({ data }) => {
  const { current, setCurrent, dirty } = useFhirEditState(data);

  return (
    // Chrome rule: an edit variant wears the chrome of the read variant it
    // replaces — AllergyReadView is a card, so this is a card.
    <Stack
      gap={3}
      data-view="allergy-edit"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          Allergy
        </Text>
        {dirty && <Tag pill subtle size="xs" color="warning" label="Edited" />}
      </Stack>
      <Grid columns={1} className="gap-x-6 gap-y-3 sm:grid-cols-2">
        <StringInput
          label="Substance"
          value={current.code?.text}
          placeholder="e.g. Penicillin"
          onChange={(next) =>
            setCurrent({ ...current, code: next ? { text: next } : undefined })
          }
        />
        <CodeSelectInput
          label="Criticality"
          value={current.criticality as Criticality | undefined}
          options={CRITICALITY_OPTIONS}
          onChange={(next) => setCurrent({ ...current, criticality: next })}
        />
        <CodeSelectInput
          label="Clinical status"
          value={
            current.clinicalStatus?.coding?.[0]?.code as
              | ClinicalStatusCode
              | undefined
          }
          options={CLINICAL_STATUS_OPTIONS}
          onChange={(next) =>
            setCurrent({
              ...current,
              clinicalStatus: clinicalStatusConcept(next),
            })
          }
        />
        <StringInput
          label="Reaction"
          value={reactionText(current)}
          placeholder="e.g. Hives"
          onChange={(next) => setCurrent(withReaction(current, next))}
        />
      </Grid>
    </Stack>
  );
};
